import { useEventBus } from '@/hooks/useEventBus.ts';

export type CustomerProfilePayload = 'customer' | 'leads' | 'sales' | 'activity';

export interface CustomerProfileInvalidation {
    customerId: number;
    payloads: readonly CustomerProfilePayload[];
}

export type CustomerProfileInvalidationHandler = (
    invalidation: CustomerProfileInvalidation
) => void | Promise<void>;

const EVENT_NAME = 'customer-profile-invalidated';
const CHANNEL_NAME = 'activix-customer-profile-invalidation';
const VALID_PAYLOADS = new Set<CustomerProfilePayload>(['customer', 'leads', 'sales', 'activity']);

const { emit, on, off } = useEventBus();
let broadcastChannel: BroadcastChannel | null | undefined;

const normalizeInvalidation = (value: unknown): CustomerProfileInvalidation | null => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const candidate = value as Partial<CustomerProfileInvalidation>;
    if (!Number.isInteger(candidate.customerId) || Number(candidate.customerId) <= 0) {
        return null;
    }
    if (
        !Array.isArray(candidate.payloads) ||
        candidate.payloads.length === 0 ||
        !candidate.payloads.every((payload) => VALID_PAYLOADS.has(payload))
    ) {
        return null;
    }
    return {
        customerId: Number(candidate.customerId),
        payloads: [...new Set(candidate.payloads)],
    };
};

const notify = (invalidation: CustomerProfileInvalidation): void => {
    emit(EVENT_NAME, invalidation);
};

const getBroadcastChannel = (): BroadcastChannel | null => {
    if (broadcastChannel !== undefined) {
        return broadcastChannel;
    }

    if (typeof BroadcastChannel === 'undefined') {
        broadcastChannel = null;
        return broadcastChannel;
    }

    try {
        broadcastChannel = new BroadcastChannel(CHANNEL_NAME);
        broadcastChannel.addEventListener('message', (event: MessageEvent<unknown>) => {
            const invalidation = normalizeInvalidation(event.data);
            if (invalidation) {
                notify(invalidation);
            }
        });
    } catch {
        broadcastChannel = null;
    }

    return broadcastChannel;
};

/**
 * Marks Customer Profile payload groups stale after a confirmed mutation.
 * Invalid runtime data and unavailable cross-tab transport fail closed so this
 * additive side effect never changes the mutation's success path.
 */
export const invalidateCustomerProfile = (value: CustomerProfileInvalidation): void => {
    const invalidation = normalizeInvalidation(value);
    if (!invalidation) {
        return;
    }

    notify(invalidation);

    try {
        getBroadcastChannel()?.postMessage(invalidation);
    } catch {
        // The same-tab notification already ran; cross-tab delivery is best effort.
    }
};

/** Adapts account Echo events without relaying them to other tabs. */
export const invalidateCustomerProfileLocally = (value: CustomerProfileInvalidation): void => {
    const invalidation = normalizeInvalidation(value);
    if (invalidation) {
        notify(invalidation);
    }
};

/**
 * Subscribes to one customer only. Signals are not replayed because Customer
 * Profile performs fresh reads on mount.
 */
export const subscribeToCustomerProfileInvalidation = (
    customerId: number,
    handler: CustomerProfileInvalidationHandler
): (() => void) => {
    if (!Number.isInteger(customerId) || customerId <= 0) {
        return () => undefined;
    }

    getBroadcastChannel();

    let disposed = false;
    let dispatchScheduled = false;
    let refreshInFlight = false;
    const pendingPayloads = new Set<CustomerProfilePayload>();

    const scheduleDispatch = (): void => {
        if (disposed || dispatchScheduled || refreshInFlight || pendingPayloads.size === 0) {
            return;
        }

        dispatchScheduled = true;
        queueMicrotask(() => {
            dispatchScheduled = false;
            if (disposed || refreshInFlight || pendingPayloads.size === 0) {
                return;
            }

            const payloads = [...pendingPayloads];
            pendingPayloads.clear();
            refreshInFlight = true;

            let refresh: void | Promise<void>;
            try {
                refresh = handler({ customerId, payloads });
            } catch {
                refresh = undefined;
            }

            Promise.resolve(refresh)
                .catch(() => undefined)
                .finally(() => {
                    refreshInFlight = false;
                    scheduleDispatch();
                });
        });
    };

    const matchingHandler = (value?: unknown): void => {
        const invalidation = normalizeInvalidation(value);
        if (invalidation?.customerId !== customerId) {
            return;
        }

        invalidation.payloads.forEach((payload) => pendingPayloads.add(payload));
        scheduleDispatch();
    };

    on(EVENT_NAME, matchingHandler);

    return () => {
        disposed = true;
        pendingPayloads.clear();
        off(EVENT_NAME, matchingHandler);
    };
};
